import { fxRatesQueryOptions } from "@/lib/fxRates";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { getAllCities } from "@/data/cities";
import { isEligibleCityListing, isKnownLocality, slugifyPlace } from "@/utils/cityArea";
import { localStockCopy, localStockRobots } from "@/utils/localStockSeo";
import { formatMakeDisplay } from '@/utils/makeDisplay';
import { getCityBySlug } from '@/data/cities';
import MasiniSecondHand from "@/pages/MasiniSecondHand";
import { routeHead } from "@/lib/seo/route-head";
import { listingsV2QueryOptions } from "@/lib/queries/publicData";

export const Route = createFileRoute("/masini-second-hand/$brand/$city/")({
  head: ({ params, loaderData }) => {
    const base = routeHead("/masini-second-hand/$brand/$city/", params as Record<string, string>);
    const rows = loaderData ?? [];
    const city = getCityBySlug(params.city, 'RO')?.name ?? params.city.replace(/-/g, ' ');
    const brand = formatMakeDisplay(params.brand.replace(/-/g, ' '));
    const copy = localStockCopy(rows, city, brand);
    return { ...base, meta: [...base.meta.filter((m) => m.name !== 'robots'), { name: 'robots', content: localStockRobots(rows.length) }],
      scripts: [{ type: 'application/ld+json', children: JSON.stringify({ '@context': 'https://schema.org', '@type': 'FAQPage',
        mainEntity: copy.faqs.map((faq) => ({ '@type': 'Question', name: faq.question, acceptedAnswer: { '@type': 'Answer', text: faq.answer } })) }) }] };
  },
  // Prefetch pentru randare server-side a listei de anunțuri (SEO).
  // Brand is matched by slug (so "skoda" finds "Škoda"); locality is exact.
  loader: async ({ context, params }) => {
    const all = await context.queryClient.ensureQueryData(
      listingsV2QueryOptions({ country_code: "RO", limit: 5000 }),
    );
    await context.queryClient.ensureQueryData(fxRatesQueryOptions()).catch(() => undefined);
    const brand = params.brand.toLowerCase();
    const city = params.city.toLowerCase();
    const brandRows = all.filter((l) => isEligibleCityListing(l) && l.make && slugifyPlace(l.make) === brand);
    // Unknown brand (no listings at all) or unknown locality is a 404.
    if (brandRows.length === 0) throw notFound();
    if (!isKnownLocality(city, getAllCities("RO").map((c) => c.slug), all as never)) throw notFound();
    return brandRows.filter((l) => l.location_city && slugifyPlace(l.location_city) === city);
  },
  errorComponent: function ListingsError({ error }: { error: unknown }) {
    return (
      <main className="container mx-auto max-w-7xl px-4 py-12">
        <h1 className="text-3xl font-bold">Anunțurile nu pot fi încărcate momentan</h1>
        <p className="mt-3 text-muted-foreground" role="alert">{error instanceof Error ? error.message : String(error)}</p>
      </main>
    );
  },
  notFoundComponent: () => (
    <main className="container mx-auto max-w-7xl px-4 py-12">
      <h1 className="text-3xl font-bold">Pagina nu a fost găsită</h1>
      <a href="/cars" className="mt-6 inline-block rounded-lg bg-primary px-5 py-3 font-medium text-primary-foreground">Caută mașini</a>
    </main>
  ),
  component: RouteComponent,
});

function RouteComponent() {
  const listings = Route.useLoaderData();
  if (!listings) throw new Error("Datele SSR pentru anunțuri lipsesc");
  return (
    <MasiniSecondHand initialListings={listings} />
  );
}
